import React, { useState } from 'react';
import { redirectToPortal } from '../utils/stripe';
import { api } from '../api/client';

// SEAT-4 — shown to a Team owner whose business has dropped to one person. They are
// paying the two seat minimum; if they work alone, Pro is billed for one. Honest
// about what moving to Pro costs (client portal timesheets, the team areas) and
// clear that nothing is deleted. Dismissable, and it will not reappear unless they
// add someone and drop back to one. No in-app plan swap: it routes to the portal.
const NAVY = '#0C2D48';
const ACCENT = '#c9d84c';

export default function SoloProOffer() {
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);

  if (hidden) return null;

  async function dismiss() {
    setHidden(true); // hide immediately for this session
    // Persist so it stays hidden across refreshes. The backend re-offers only after
    // they add someone and drop back to one (the add-then-drop reset).
    try { await api('/api/team/solo-pro-offer/dismiss', { method: 'POST' }); }
    catch (err) { console.error('[SoloProOffer] dismiss failed:', err); }
  }

  async function moveToPro() {
    setBusy(true);
    try { await redirectToPortal({ fallbackPlanName: 'pro', fallbackBillingPeriod: 'monthly' }); }
    finally { setBusy(false); }
  }

  return (
    <div style={{
      position: 'relative', margin: '0.75rem 1rem', padding: '1.1rem 1.25rem',
      background: '#fff', border: `1px solid #e3e8ee`, borderLeft: `4px solid ${ACCENT}`,
      borderRadius: 12, boxShadow: '0 2px 10px rgba(12,45,72,0.06)',
      fontFamily: 'Raleway, sans-serif', maxWidth: 820,
    }}>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          position: 'absolute', top: 8, right: 10, background: 'none', border: 'none',
          fontSize: '1.1rem', lineHeight: 1, color: '#aaa', cursor: 'pointer', padding: 4,
        }}
      >
        ×
      </button>

      <h3 style={{ margin: '0 0 0.5rem', fontFamily: 'Poppins, sans-serif', fontSize: '1.02rem', fontWeight: 800, color: NAVY }}>
        Working on your own? You could move to Pro
      </h3>

      <p style={{ margin: '0 0 0.6rem', fontSize: '0.9rem', color: '#445', lineHeight: 1.6 }}>
        You are on the Team plan, which has a two seat minimum, so you are currently
        paying for two people even though it is just you. Pro is our single person plan
        and is billed for one.
      </p>

      <p style={{ margin: '0 0 0.4rem', fontSize: '0.86rem', fontWeight: 700, color: NAVY }}>
        Before you switch, one thing to know:
      </p>
      <ul style={{ margin: '0 0 0.6rem', paddingLeft: '1.1rem', fontSize: '0.86rem', color: '#445', lineHeight: 1.6 }}>
        <li>
          On Pro, your clients can no longer see the time you have logged against their
          projects in the client portal. That client portal timesheets view is a Team feature.
        </li>
        <li>
          The team areas (Team Calendar, Team Dashboard and HR) switch off. While you are
          working alone these will not matter.
        </li>
      </ul>

      <p style={{ margin: '0 0 0.9rem', fontSize: '0.86rem', color: '#445', lineHeight: 1.6 }}>
        Everything else you use carries over, including your own time tracking, invoicing,
        proposals, contracts, bookings and financials. Nothing is deleted. If you add
        someone again and move back to Team, all of it switches back on.
      </p>

      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={moveToPro}
          disabled={busy}
          style={{
            background: NAVY, color: '#fff', border: 'none', borderRadius: 8,
            padding: '0.55rem 1.1rem', fontWeight: 700, fontSize: '0.86rem',
            cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1, fontFamily: 'Raleway, sans-serif',
          }}
        >
          {busy ? 'Opening billing…' : 'Move to Pro in the billing portal'}
        </button>
        <button
          onClick={dismiss}
          style={{
            background: 'none', border: 'none', color: '#888', fontSize: '0.84rem',
            textDecoration: 'underline', cursor: 'pointer', fontFamily: 'Raleway, sans-serif',
          }}
        >
          Not now
        </button>
      </div>
    </div>
  );
}
